// Pure tree helpers for the element node tree (map/update/delete/insert/move/reorder).
// Extracted from editor.store; no mutation, no store access. Types are import-only
// (erased at runtime) so there is no runtime cycle with the store.
import type { ContentBinding, ElementNode, Page } from './editor.store'

// ─── Tree helpers ─────────────────────────────────────────────────────────────
// Pure functions — no mutation. Used by store actions.

export function mapTree(
  nodes: ElementNode[],
  fn: (node: ElementNode) => ElementNode
): ElementNode[] {
  return nodes.map((node) => {
    const updated = fn(node)
    return { ...updated, children: mapTree(updated.children, fn) }
  })
}

export function updateNodeInTree(
  nodes: ElementNode[],
  id: string,
  updates: Partial<ElementNode>
): ElementNode[] {
  return mapTree(nodes, (node) => (node.id === id ? { ...node, ...updates } : node))
}

export function deleteNodeFromTree(nodes: ElementNode[], id: string): ElementNode[] {
  return nodes
    .filter((node) => node.id !== id)
    .map((node) => ({ ...node, children: deleteNodeFromTree(node.children, id) }))
}

export function addNodeToTree(
  nodes: ElementNode[],
  parentId: string | null,
  newNode: ElementNode
): ElementNode[] {
  if (parentId === null) {
    return [...nodes, newNode]
  }
  return mapTree(nodes, (node) =>
    node.id === parentId
      ? { ...node, children: [...node.children, newNode] }
      : node
  )
}

export function clearContentBindings(
  nodes: ElementNode[],
  predicate: (binding: ContentBinding) => boolean,
): ElementNode[] {
  return nodes.map((node) => {
    const binding = node.contentBinding?.source === 'projectContent' ? node.contentBinding : undefined
    const repeat = node.cmsRepeat?.source === 'projectContent' ? node.cmsRepeat : undefined
    const nextBinding = binding && predicate(binding) ? undefined : node.contentBinding
    const nextRepeat = repeat && predicate({ source: 'projectContent', collection: repeat.collection, field: repeat.sortField ?? '' }) ? undefined : node.cmsRepeat
    return {
      ...node,
      ...(nextBinding !== node.contentBinding ? { contentBinding: nextBinding } : {}),
      ...(nextRepeat !== node.cmsRepeat ? { cmsRepeat: nextRepeat } : {}),
      children: clearContentBindings(node.children, predicate),
    }
  })
}

export function clearProjectBindings(
  pages: Page[],
  predicate: (binding: ContentBinding) => boolean,
): Page[] {
  return pages.map((page) => ({
    ...page,
    elements: clearContentBindings(page.elements, predicate),
  }))
}

/**
 * Insert newNode relative to targetId. Falls back to appending at root if
 * targetId is not found (e.g. canvas is empty).
 */
export function insertNodeAtTarget(
  nodes: ElementNode[],
  newNode: ElementNode,
  targetId: string,
  position: 'before' | 'after' | 'inside'
): ElementNode[] {
  let inserted = false
  function insert(ns: ElementNode[]): ElementNode[] {
    const result: ElementNode[] = []
    for (const n of ns) {
      if (!inserted && n.id === targetId) {
        inserted = true
        if (position === 'before')     result.push(newNode, n)
        else if (position === 'after') result.push(n, newNode)
        else                           result.push({ ...n, children: [...n.children, newNode] })
      } else {
        result.push({ ...n, children: insert(n.children) })
      }
    }
    return result
  }
  const result = insert(nodes)
  return inserted ? result : [...nodes, newNode] // fallback: append at root
}

export function isDescendant(node: ElementNode, id: string): boolean {
  return node.children.some((c) => c.id === id || isDescendant(c, id))
}

/** Return the path from root to the element with the given id, or null if not found */
export function findPathToElement(nodes: ElementNode[], id: string): ElementNode[] | null {
  for (const node of nodes) {
    if (node.id === id) return [node]
    const childPath = findPathToElement(node.children, id)
    if (childPath) return [node, ...childPath]
  }
  return null
}

/** Find the parent of an element in the tree. Returns null if at root level. */
export function findParentElement(nodes: ElementNode[], id: string): ElementNode | null {
  for (const node of nodes) {
    if (node.children.some((c) => c.id === id)) return node
    const found = findParentElement(node.children, id)
    if (found) return found
  }
  return null
}

/** Deep-copy a node tree with freshly generated IDs */
export function regenIds(node: ElementNode): ElementNode {
  return { ...node, id: crypto.randomUUID(), children: node.children.map(regenIds) }
}

/**
 * Las piezas que no se sostienen solas.
 *
 * Un disparador sin su contenedor es un botón que no abre nada, y un panel sin
 * su botón es contenido que nadie puede sacar: quien abre y cierra es el bloque
 * de arriba —el que lleva `behavior`—, no ninguna de las dos mitades.
 */
const WIDGET_PARTS = new Set(['wb-popover-trigger', 'wb-popover-panel'])

/**
 * El nodo que de verdad hay que copiar.
 *
 * Copiar el botón de una ventana daba un botón muerto: se pegaba, se veía igual
 * y no abría nada, sin ninguna señal de por qué. Y es lo que sale de hacer lo
 * natural —entrar al bloque, pulsar el botón, copiar—, porque lo que se ve y se
 * selecciona es el botón mientras que lo que funciona es su contenedor.
 *
 * Así que al copiar una mitad se sube al bloque entero. No hay caso en que
 * alguien quiera de verdad un disparador suelto: siempre es el error.
 */
export function copyableRoot(nodes: ElementNode[], id: string): ElementNode | undefined {
  const path = findPathToElement(nodes, id)
  if (!path) return undefined
  const target = path[path.length - 1]
  if (!target || !target.schemaId || !WIDGET_PARTS.has(target.schemaId)) return target
  /* El popover más cercano hacia arriba. Con `reverse` y no el primero: una
     ventana dentro de otra tiene dos, y la que manda es la de dentro. */
  return path.slice(0, -1).reverse().find((node) => node.behavior?.type === 'popover') ?? target
}

/**
 * Quita el `schemaId` a las mitades que llegaron sin su bloque.
 *
 * Un botón huérfano conservaba `wb-popover-trigger`, y eso es peor que
 * inofensivo: el guion del sitio busca el disparador con `querySelector`, que
 * devuelve el PRIMERO del bloque. Pegado dentro de otra ventana, ese botón
 * muerto le robaba el papel al disparador de verdad y rompía una ventana que
 * funcionaba — un fallo a distancia, en un sitio que nadie tocó.
 *
 * Sin `schemaId` es lo que aparenta: un botón normal.
 */
export function stripOrphanWidgetParts(node: ElementNode, insidePopover = false): ElementNode {
  const isPopover = insidePopover || node.behavior?.type === 'popover'
  const orphan = !insidePopover && !!node.schemaId && WIDGET_PARTS.has(node.schemaId)
  const children = node.children.map((child) => stripOrphanWidgetParts(child, isPopover))
  if (!orphan) return { ...node, children }
  /* Se borra en vez de ponerla a `undefined`: con `exactOptionalPropertyTypes`
     una clave presente y vacía no es lo mismo que una clave ausente, y aguas
     abajo hay sitios que preguntan por la clave, no por su valor. */
  const stripped: ElementNode = { ...node, children }
  delete stripped.schemaId
  return stripped
}

/**
 * Wrap the nodes identified by `ids` (which must be siblings) in a new div.
 * The wrapper is inserted at the position of the first matched sibling.
 */
export function wrapNodesInDiv(
  nodes: ElementNode[],
  ids: string[],
  wrapperId: string
): ElementNode[] {
  const idSet = new Set(ids)
  const presentHere = ids.filter((id) => nodes.some((n) => n.id === id))

  if (presentHere.length === ids.length) {
    // All targets are siblings at this level — wrap them in order
    const childrenToWrap = nodes.filter((n) => idSet.has(n.id))
    const wrapper: ElementNode = {
      id: wrapperId,
      type: 'div',
      label: 'Bloque',
      children: childrenToWrap,
      styles: { desktop: { display: 'flex', flexDirection: 'column', gap: '16px' } },
    }
    const result: ElementNode[] = []
    let inserted = false
    for (const n of nodes) {
      if (idSet.has(n.id)) {
        if (!inserted) { result.push(wrapper); inserted = true }
        // skip — moved inside wrapper
      } else {
        result.push(n)
      }
    }
    return result
  }

  // Not all at this level — recurse into children
  return nodes.map((n) => ({ ...n, children: wrapNodesInDiv(n.children, ids, wrapperId) }))
}

/**
 * Adelanta o atrasa un nodo entre sus hermanos.
 *
 * `steps` es cuántos puestos, y existe por las cuadrículas: ahí «arriba» no es
 * el hermano anterior sino el de la fila de encima, que está tantos puestos
 * atrás como columnas tenga. Con un puesto, sacar y volver a meter es lo mismo
 * que intercambiar con el vecino —que es lo que hacía antes—; con más de uno no,
 * y es sacar y meter lo que hay que hacer: intercambiar dos lejanos deja al otro
 * fuera de su sitio.
 *
 * El destino se recorta a los extremos en vez de rechazarse: bajando desde la
 * penúltima fila de una cuadrícula incompleta, el puesto de debajo puede no
 * existir y aun así «abajo del todo» es exactamente lo que se está pidiendo.
 */
function reorderNodeInTreeRaw(
  nodes: ElementNode[],
  elementId: string,
  direction: 'up' | 'down',
  steps = 1
): ElementNode[] {
  const idx = nodes.findIndex((n) => n.id === elementId)
  if (idx !== -1) {
    const jump = Math.max(1, Math.round(steps))
    const target = Math.max(0, Math.min(nodes.length - 1, direction === 'up' ? idx - jump : idx + jump))
    if (target === idx) return nodes
    const arr = [...nodes]
    const [moved] = arr.splice(idx, 1)
    if (!moved) return nodes
    arr.splice(target, 0, moved)
    return arr
  }
  return nodes.map((n) => ({ ...n, children: reorderNodeInTreeRaw(n.children, elementId, direction, steps) }))
}

/**
 * Move elementId to a new position relative to targetId.
 * position 'before'/'after' → sibling of target
 * position 'inside'         → appended as last child of target
 * Returns the original array unchanged if the move is invalid
 * (element not found, circular reference, same element).
 */
function moveNodeInTreeRaw(
  nodes: ElementNode[],
  elementId: string,
  targetId: string,
  position: 'before' | 'after' | 'inside'
): ElementNode[] {
  // Step 1: extract the moving element from the tree
  let moved: ElementNode | undefined
  function extract(ns: ElementNode[]): ElementNode[] {
    return ns
      .filter((n) => {
        if (n.id === elementId) { moved = n; return false }
        return true
      })
      .map((n) => ({ ...n, children: extract(n.children) }))
  }
  const withoutEl = extract(nodes)

  if (!moved) return nodes                                  // element not found
  if (moved.id === targetId) return nodes                   // can't drop onto itself
  if (isDescendant(moved, targetId)) return nodes           // can't drop into own subtree

  // Step 2: insert at new position
  function insert(ns: ElementNode[]): ElementNode[] {
    const result: ElementNode[] = []
    for (const n of ns) {
      if (n.id === targetId) {
        if (position === 'before')       result.push(moved!, n)
        else if (position === 'after')   result.push(n, moved!)
        else /* inside */                result.push({ ...n, children: [...n.children, moved!] })
      } else {
        result.push({ ...n, children: insert(n.children) })
      }
    }
    return result
  }
  return insert(withoutEl)
}

export function findParentInfo(
  nodes: ElementNode[],
  elementId: string,
  parentId: string | null = null
): { parentId: string | null; index: number } | null {
  const index = nodes.findIndex((node) => node.id === elementId)
  if (index !== -1) return { parentId, index }

  for (const node of nodes) {
    const found = findParentInfo(node.children, elementId, node.id)
    if (found) return found
  }
  return null
}

function moveNodeToParentIndexRaw(
  nodes: ElementNode[],
  elementId: string,
  parentId: string | null,
  index: number
): ElementNode[] {
  if (parentId === elementId) return nodes

  const movingNode = findNodeById(nodes, elementId)
  if (!movingNode) return nodes
  if (parentId && isDescendant(movingNode, parentId)) return nodes

  const originalParent = findParentInfo(nodes, elementId)
  if (!originalParent) return nodes

  let moved: ElementNode | undefined
  function extract(children: ElementNode[]): ElementNode[] {
    const result: ElementNode[] = []
    for (const child of children) {
      if (child.id === elementId) {
        moved = child
      } else {
        result.push({ ...child, children: extract(child.children) })
      }
    }
    return result
  }

  const withoutMoved = extract(nodes)
  if (!moved) return nodes

  const adjustedIndex =
    originalParent.parentId === parentId && originalParent.index < index
      ? index - 1
      : index

  function insertAt(children: ElementNode[]): ElementNode[] {
    const targetIndex = Math.max(0, Math.min(adjustedIndex, children.length))
    return [
      ...children.slice(0, targetIndex),
      moved!,
      ...children.slice(targetIndex),
    ]
  }

  if (parentId === null) {
    if (originalParent.parentId === null && originalParent.index === adjustedIndex) return nodes
    return insertAt(withoutMoved)
  }

  let inserted = false
  function insertIntoParent(children: ElementNode[]): ElementNode[] {
    return children.map((child) => {
      if (child.id === parentId) {
        inserted = true
        return { ...child, children: insertAt(child.children) }
      }
      return { ...child, children: insertIntoParent(child.children) }
    })
  }

  const next = insertIntoParent(withoutMoved)
  return inserted ? next : nodes
}

/** Un tab y su panel se reordenan juntos desde canvas, Capas y sus previews. */
function syncTabsPairOrder(before: ElementNode[], after: ElementNode[], elementId: string): ElementNode[] {
  const path = findPathToElement(before, elementId)
  const parent = path?.at(-2)
  if (parent?.schemaId !== 'wb-tabs-nav' && parent?.schemaId !== 'wb-tabs-track') return after
  const host = path!.slice(0, -2).reverse().find((node) => node.behavior?.type === 'tabs')
  if (!host) return after
  const counterpartSchema = parent.schemaId === 'wb-tabs-nav' ? 'wb-tabs-track' : 'wb-tabs-nav'
  const findPart = (node: ElementNode): ElementNode | undefined => {
    if (node.schemaId === counterpartSchema) return node
    for (const child of node.children) {
      if (child.behavior?.type === 'tabs') continue
      const found = findPart(child)
      if (found) return found
    }
    return undefined
  }
  const counterpart = findPart(host)
  const movedParent = findNodeById(after, parent.id)
  if (!counterpart || counterpart.children.length !== parent.children.length || !movedParent
    || movedParent.children.length !== parent.children.length) return before
  const originalIndexes = new Map(parent.children.map((child, index) => [child.id, index]))
  if (movedParent.children.some((child) => !originalIndexes.has(child.id))) return before
  return updateNodeInTree(after, counterpart.id, {
    children: movedParent.children.map((child) => counterpart.children[originalIndexes.get(child.id)!]!),
  })
}

export function reorderNodeInTree(nodes: ElementNode[], elementId: string, direction: 'up' | 'down', steps = 1): ElementNode[] {
  return syncTabsPairOrder(nodes, reorderNodeInTreeRaw(nodes, elementId, direction, steps), elementId)
}

export function moveNodeInTree(nodes: ElementNode[], elementId: string, targetId: string, position: 'before' | 'after' | 'inside'): ElementNode[] {
  return syncTabsPairOrder(nodes, moveNodeInTreeRaw(nodes, elementId, targetId, position), elementId)
}

export function moveNodeToParentIndex(nodes: ElementNode[], elementId: string, parentId: string | null, index: number): ElementNode[] {
  return syncTabsPairOrder(nodes, moveNodeToParentIndexRaw(nodes, elementId, parentId, index), elementId)
}

export function findNodeById(nodes: ElementNode[], id: string): ElementNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node
    const found = findNodeById(node.children, id)
    if (found) return found
  }
  return undefined
}
